import _ from 'underscore';
import { v4 } from 'uuid';

import { SkyMapRootScope } from '../core/app';
import { TokenService } from '../services/authentication/token.service';
import {
  CerbosApiService,
  CerbosPrincipalAttributes,
  CerbosProjectRoles,
  CerbosRequestData,
  CerbosViewScope,
} from '../services/cerbos/cerbos-api';
import { cerbosControlledFeatures, CerbosFeature } from '../services/cerbos/features';
import { cerbosControlledViews, CerbosView } from '../services/cerbos/views';
import { ThemeManager } from '../theming/theme-manager';
import { isDefined } from '../utils/variables';
import { CompanyStore } from './company.store';
import { ProjectStore } from './project-store';
import { UserStore } from './user-store';

export enum CerbosPermissionStatus {
  ACCEPT,
  DENY,
  // This is temporary while we transition to Cerbos system,
  // and returns if the current resource is not implemented yet,
  NOT_DEFINED,
}

interface CerbosAttributeProject {
  id: string;
  companyId: string;
}

export class PermissionStore {
  private static _instance = new PermissionStore();
  static get instance() {
    return PermissionStore._instance;
  }

  private cerbosApiService = new CerbosApiService();

  private allowedAccessKeys = new Set<string>();

  private rootScope?: SkyMapRootScope;

  private currentPermissionProjectId?: string;

  setRootScope(rootScope: SkyMapRootScope) {
    this.rootScope = rootScope;
  }

  requirePermissionsReload(projectId?: string) {
    return this.currentPermissionProjectId !== projectId || this.allowedAccessKeys.size <= 0;
  }

  async refreshAccessState(projectId?: string) {
    const resourceProjectId = projectId ?? ProjectStore.instance.project?.id;

    const tokenService = new TokenService();
    const token = tokenService.get();

    const accessResponse = await Promise.all([
      this.executeCerbosAccessRequest({
        requestId: v4(),
        principal: { attributes: await this.resolveCerbosPrincipalAttributes() },
        resource: {
          attributes: { projectId: resourceProjectId },
          kind: 'view',
          scope: this.resolveViewScope(),
        },
        actions: Array.from(cerbosControlledViews),
        jwtToken: token,
      } as CerbosRequestData),
      await this.executeCerbosAccessRequest({
        requestId: v4(),
        principal: { attributes: await this.resolveCerbosPrincipalAttributes() },
        resource: {
          attributes: { projectId: resourceProjectId },
          kind: 'feature',
          scope: this.resolveViewScope(),
        },
        actions: Array.from(cerbosControlledFeatures),
        jwtToken: token,
      } as CerbosRequestData),
    ]);

    this.currentPermissionProjectId = resourceProjectId;

    this.allowedAccessKeys = new Set(
      _.flatten(accessResponse.map((response) => response.allowedActions())),
    );

    // Need to inform angular whenever the permission data have been updated.
    // TODO: Cleanup once AngularJs is removed.
    this.rootScope?.$applyAsync();

    return accessResponse;
  }

  async executeCerbosAccessRequest(requestData: CerbosRequestData) {
    return await this.cerbosApiService.checkAccess(requestData);
  }

  checkAccessToView(view?: CerbosView) {
    if (!isDefined(view) || !cerbosControlledViews.has(view)) {
      return CerbosPermissionStatus.NOT_DEFINED;
    }
    return this.allowedAccessKeys.has(view)
      ? CerbosPermissionStatus.ACCEPT
      : CerbosPermissionStatus.DENY;
  }

  checkAccessToFeature(feature?: CerbosFeature) {
    if (!isDefined(feature) || !cerbosControlledFeatures.has(feature)) {
      return CerbosPermissionStatus.NOT_DEFINED;
    }
    return this.allowedAccessKeys.has(feature)
      ? CerbosPermissionStatus.ACCEPT
      : CerbosPermissionStatus.DENY;
  }

  // Fetches all of the user and company projects,
  // and adds them to the Cerbos policy check.
  async resolveCerbosPrincipalAttributes(): Promise<CerbosPrincipalAttributes> {
    const companyProjects = _.flatten(
      CompanyStore.instance.getCompanies()?.map((company) => company.Projects) ?? [],
    );

    return {
      projects: await this.resolveProjects(
        companyProjects as CerbosAttributeProject[],
        (UserStore.instance.getProjects() ?? []) as CerbosAttributeProject[],
      ),
      companyUsers: UserStore.instance.getCompanyUsers() ?? [],
      isSharedView: ThemeManager.instance.isSharedView,
    };
  }

  private async resolveProjects(
    companyProjects: CerbosAttributeProject[],
    userProjects: CerbosAttributeProject[],
  ) {
    const seats = (await UserStore.instance.getActiveUser())?.designToolSeats ?? [];

    const currentTime = new Date().getTime();
    const hasDesignSeatInProject = (companyId: string) => {
      return seats.some(
        (seat) =>
          seat.companyId === companyId &&
          (!isDefined(seat.validUntil) || new Date(seat.validUntil).getTime() > currentTime),
      );
    };

    // Remove any user projects if the user also is a company admin for that project.
    const filteredUserProjects = userProjects.filter(
      (project) => !_.find(companyProjects, { id: project.id }),
    );

    const buildProjectAttributes = (project: CerbosAttributeProject, role: CerbosProjectRoles) => ({
      id: project.id,
      role,
      hasDesignSeat: hasDesignSeatInProject(project.companyId),
    });

    return [
      ...companyProjects.map((project) => buildProjectAttributes(project, 'admin')),
      ...filteredUserProjects.map((project) => buildProjectAttributes(project, 'user')),
    ];
  }

  private resolveViewScope(): CerbosViewScope | undefined {
    const theme = ThemeManager.instance.theme;
    switch (theme.themeName) {
      case 'moba':
        return 'moba';
      case 'default':
        return 'skymap';
      case 'zynka':
        return 'zynka';
      default:
        return undefined;
    }
  }
}
